import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, X, MessageCircle, MapPin, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useCountry } from '@/contexts/CountryContext';
import { cn } from '@/lib/utils';

const SUGGESTIONS = {
  albania: ['3 perfect days in Saranda', 'Best Tirana nightlife', 'Hidden beaches near Ksamil'],
  kosovo: ['Weekend in Prizren', 'Rugova Canyon adventure', 'Pristina café tour'],
  'north-macedonia': ['2 days in Ohrid', 'Matka Canyon day trip', 'Skopje to Bitola road trip'],
};

const SESSION_KEY = 'infopoint_chat_session';

// Renders simple markdown-ish formatting: **bold**, line breaks, bullets and headings.
const FormattedMessage = ({ text }) => {
  const lines = text.split('\n');
  return (
    <div className="space-y-1.5 text-sm leading-relaxed">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-1" />;
        if (line.startsWith('# ')) return <div key={i} className="font-heading text-base font-bold mt-1.5">{line.slice(2)}</div>;
        if (line.startsWith('## ')) return <div key={i} className="font-heading text-sm font-bold text-[#00AA6C] mt-1.5">{line.slice(3)}</div>;
        if (line.startsWith('- ') || line.startsWith('* ')) return <div key={i} className="pl-2 text-gray-700"><span className="text-[#00AA6C] mr-1">•</span>{formatBold(line.slice(2))}</div>;
        if (/^\d+\.\s/.test(line)) return <div key={i} className="pl-2 text-gray-700">{formatBold(line)}</div>;
        if (line.startsWith('---')) return <hr key={i} className="my-1.5 border-gray-200" />;
        return <div key={i} className="text-gray-700">{formatBold(line)}</div>;
      })}
    </div>
  );
};

const formatBold = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} className="font-semibold text-gray-900">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
};

export const AIChatPanel = ({ compact = false }) => {
  const { country } = useCountry();
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(SESSION_KEY) || ''; } catch { return ''; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const suggestions = SUGGESTIONS[country.code] || SUGGESTIONS.albania;

  const send = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const res = await api.chat(sessionId || null, text);
      if (!sessionId) {
        setSessionId(res.session_id);
        try { localStorage.setItem(SESSION_KEY, res.session_id); } catch {}
      }
      setMessages((m) => [...m, { role: 'assistant', content: res.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: '⚠️ Sorry, I had trouble reaching the server. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-[#00AA6C] to-[#008F5A] text-white">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-semibold text-sm">Balkans Concierge</div>
            <div className="text-[10px] text-white/80 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-300" /> Online · Claude Sonnet 4.5
            </div>
          </div>
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full bg-white/15 border border-white/20">{country.flag} {country.name}</span>
      </div>

      <div className={cn('flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50', compact ? 'min-h-[360px] max-h-[60vh]' : 'min-h-[460px]')} data-testid="ai-chat-messages">
        {messages.length === 0 && !loading && (
          <div className="text-center py-8">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#00AA6C]/10 flex items-center justify-center mb-3">
              <Sparkles className="w-7 h-7 text-[#00AA6C]" />
            </div>
            <h4 className="font-heading font-bold text-gray-900 mb-1">Tell us your dream trip</h4>
            <p className="text-xs text-gray-500 max-w-xs mx-auto">Itineraries, restaurants, hidden beaches, nightlife — ask anything about {country.name}.</p>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
            {m.role === 'assistant'
              ? (
                <div className="max-w-[88%] px-3.5 py-2.5 rounded-2xl rounded-bl-sm bg-white border border-gray-200 shadow-sm" data-testid="ai-message-assistant">
                  <FormattedMessage text={m.content} />
                </div>
              )
              : (
                <div className="max-w-[80%] px-3.5 py-2 rounded-2xl rounded-br-sm bg-[#00AA6C] text-white text-sm shadow-sm" data-testid="ai-message-user">
                  {m.content}
                </div>
              )
            }
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="px-3.5 py-2.5 rounded-2xl rounded-bl-sm bg-white border border-gray-200 shadow-sm flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#00AA6C] animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 rounded-full bg-[#00AA6C] animate-bounce" style={{ animationDelay: '120ms' }} />
              <span className="w-2 h-2 rounded-full bg-[#00AA6C] animate-bounce" style={{ animationDelay: '240ms' }} />
              <span className="text-xs text-gray-500 ml-1">Crafting your itinerary…</span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {messages.length === 0 && (
        <div className="px-4 pb-2 flex flex-wrap gap-1.5 bg-gray-50 border-t border-gray-100">
          {suggestions.map((s) => (
            <button key={s} onClick={() => send(s)} className="text-xs px-2.5 py-1 rounded-full bg-white border border-gray-200 hover:border-[#00AA6C] hover:text-[#00AA6C] text-gray-700 transition-colors" data-testid={`ai-suggestion-${s}`}>
              {s}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="border-t bg-white p-3 flex items-center gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Ask about ${country.name}…`}
          disabled={loading}
          className="bg-gray-50 border-gray-200 focus-visible:ring-[#00AA6C]"
          data-testid="ai-chat-input"
        />
        <Button type="submit" variant="green" size="icon" disabled={!input.trim() || loading} data-testid="ai-chat-send">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
};

// Floating chat bubble for global use
export const AIChatBubble = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={cn(
          'fixed bottom-6 left-6 z-40 group flex items-center gap-2 px-4 h-12 rounded-full bg-[#00AA6C] hover:bg-[#008F5A] text-white shadow-2xl shadow-[#00AA6C]/40 transition-all duration-300',
          open && 'opacity-0 pointer-events-none scale-90'
        )}
        aria-label="Open AI concierge"
        data-testid="ai-bubble-open"
      >
        <Sparkles className="w-5 h-5" />
        <span className="font-semibold text-sm hidden sm:inline">Ask AI Concierge</span>
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#D32323] ring-2 ring-white animate-pulse" />
      </button>

      <div
        className={cn(
          'fixed inset-0 bg-black/30 backdrop-blur-sm z-50 transition-opacity duration-300',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={() => setOpen(false)}
      />
      <div
        className={cn(
          'fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[420px] h-[80vh] sm:h-[640px] sm:max-h-[85vh] bg-white sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col transition-all duration-300 origin-bottom-right',
          open ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 pointer-events-none scale-95'
        )}
        data-testid="ai-chat-window"
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur text-white flex items-center justify-center"
          aria-label="Close"
          data-testid="ai-bubble-close"
        >
          <X className="w-4 h-4" />
        </button>
        <AIChatPanel compact />
      </div>
    </>
  );
};

// Large hero section variant for use on homepages / dedicated /ai page
export const AIAssistantSection = () => {
  const { country } = useCountry();
  return (
    <section className="relative py-20 bg-gradient-to-br from-[#0F1929] to-[#1A2A40] overflow-hidden" data-testid="ai-assistant-section">
      <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] rounded-full bg-[#00AA6C]/20 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#D32323]/15 blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="text-white">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-medium mb-5">
              <Sparkles className="w-3 h-3 text-[#00AA6C]" /> AI Concierge · Powered by Claude Sonnet 4.5
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold leading-tight mb-5">
              Tell us your dream trip.<br />
              <span className="text-[#00AA6C]">We'll write it for you.</span>
            </h2>
            <p className="text-base sm:text-lg text-white/75 mb-7 max-w-md leading-relaxed">
              Our concierge knows every hidden beach, every boutique table and every secret rooftop in {country.name}. Ask anything — multi-day itineraries, restaurants, transport.
            </p>
            <div className="grid grid-cols-3 gap-3 max-w-md">
              {[
                { icon: MapPin, label: '6,400+ places' },
                { icon: Calendar, label: 'Custom itineraries' },
                { icon: MessageCircle, label: '24/7 instant' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="bg-white/5 backdrop-blur rounded-xl p-3 border border-white/10">
                  <Icon className="w-4 h-4 text-[#00AA6C] mb-2" />
                  <div className="text-xs text-white/85 font-medium leading-snug">{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-[#00AA6C]/40 to-[#D32323]/30 blur-3xl pointer-events-none" />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-white">
              <AIChatPanel compact />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
